export enum EventSponsorEventType {
  SimpleEvent = 'SimpleEvent',
  Conference = 'Conference',
}

export type EventSponsorTemplateOption = {
  value: string;
  label: string;
  description: string;
};

export const ADD_TIER_OPTION_VALUE = 'Add Tier';
export const MAX_CONFERENCE_TIERS = 10;
export const CONFERENCE_TIER_LIMIT_MESSAGE =
  'Conference tiers can only add up to 10 total tiers.';
export const CUSTOM_CONFERENCE_TIERS = [
  'Tier 4',
  'Tier 5',
  'Tier 6',
  'Tier 7',
  'Tier 8',
  'Tier 9',
  'Tier 10',
] as const;

export const EVENT_TYPE_OPTIONS: Array<{
  value: EventSponsorEventType;
  label: string;
  description: string;
}> = [
  {
    value: EventSponsorEventType.SimpleEvent,
    label: 'Simple Event',
    description: 'Use Featured Sponsor and Additional Sponsor templates.',
  },
  {
    value: EventSponsorEventType.Conference,
    label: 'Conference',
    description:
      'Use Tier 1, Tier 2, Tier 3 templates. Rename and add more tiers later.',
  },
];

export const SIMPLE_EVENT_BASE_TEMPLATES: EventSponsorTemplateOption[] = [
  {
    value: 'Featured Sponsor',
    label: 'Featured Sponsor',
    description:
      'The primary sponsor shown prominently on the event page and communications.',
  },
  {
    value: 'Additional Sponsors',
    label: 'Additional Sponsors',
    description:
      'Secondary sponsors listed below the featured sponsor on the event page.',
  },
];

export const CONFERENCE_BASE_TEMPLATES: EventSponsorTemplateOption[] = [
  {
    value: 'Tier 1',
    label: 'Tier 1',
    description:
      'Highest-priority conference template. Rename later, e.g. Diamond Partner.',
  },
  {
    value: 'Tier 2',
    label: 'Tier 2',
    description:
      'Second-priority conference template. Rename later, e.g. Presenting Partner.',
  },
  {
    value: 'Tier 3',
    label: 'Tier 3',
    description:
      'Third-priority conference template. Rename later, e.g. Community Partner.',
  },
  {
    value: ADD_TIER_OPTION_VALUE,
    label: 'Add Tier',
    description:
      'Create a custom tier beyond the standard three conference levels.',
  },
];

export const getBaseTemplatesForEventType = (
  eventType: EventSponsorEventType | ''
): EventSponsorTemplateOption[] => {
  if (eventType === EventSponsorEventType.SimpleEvent) {
    return SIMPLE_EVENT_BASE_TEMPLATES;
  }
  if (eventType === EventSponsorEventType.Conference) {
    return CONFERENCE_BASE_TEMPLATES;
  }
  return [];
};

export const getEventTypeLabel = (eventType: string) => {
  return (
    EVENT_TYPE_OPTIONS.find((option) => option.value === eventType)?.label ??
    eventType
  );
};

export const getNextCustomConferenceTierName = (
  existingBaseTemplates: string[]
): string | null =>
  CUSTOM_CONFERENCE_TIERS.find(
    (tierName) => !existingBaseTemplates.includes(tierName)
  ) ?? null;

export const isConferenceTierLimitReached = ({
  existingBaseTemplates,
  eventType,
  isEditMode,
}: {
  existingBaseTemplates: string[];
  eventType: string;
  isEditMode: boolean;
}) =>
  !isEditMode &&
  eventType === EventSponsorEventType.Conference &&
  existingBaseTemplates.length >= MAX_CONFERENCE_TIERS;

export const resolveBaseTemplate = (
  baseTemplate: string,
  nextCustomName?: string | null
) =>
  baseTemplate === ADD_TIER_OPTION_VALUE
    ? nextCustomName ?? ADD_TIER_OPTION_VALUE
    : baseTemplate;

export const toBaseTemplateOptionValue = (baseTemplate: string) =>
  CUSTOM_CONFERENCE_TIERS.includes(
    baseTemplate as (typeof CUSTOM_CONFERENCE_TIERS)[number]
  )
    ? ADD_TIER_OPTION_VALUE
    : baseTemplate;
